import { useQuery } from '@tanstack/react-query'
import { webApi } from '@/services/webApi'
import type { ApiFilterOptions } from '@/types/api'
import {
  BHK_FILTER_OPTIONS,
  PRICE_FILTER_PRESETS,
  PROPERTY_TYPE_OPTIONS,
  SALE_TYPE_OPTIONS,
} from '@/lib/propertyFilterOptions'
import { SORT_OPTIONS } from '@/lib/propertyFilterUtils'

const FALLBACK: ApiFilterOptions = {
  cities: [],
  categories: PROPERTY_TYPE_OPTIONS.map((name, index) => ({
    id: index + 1,
    name,
    count: 0,
  })),
  areas: [],
  intents: [
    { value: 'buy', label: 'Buy' },
    { value: 'rent', label: 'Rent' },
    { value: 'commercial', label: 'Commercial' },
  ],
  bhkOptions: [...BHK_FILTER_OPTIONS],
  furnishedOptions: ['Furnished', 'Semi-Furnished', 'Unfurnished'],
  saleTypeOptions: SALE_TYPE_OPTIONS.map((label) => ({
    label,
    value: label === 'New Bookings' ? 'New Property' : 'Old Property',
  })),
  budgetRanges: PRICE_FILTER_PRESETS.filter((p) => p.min != null || p.max != null).map((p) => ({
    label: p.label,
    min: p.min ?? 0,
    max: p.max ?? null,
  })),
  priceSlider: { min: 0, max: 200_000_000, step: 100_000 },
  sortOptions: [...SORT_OPTIONS],
}

export function useFilterOptions(city?: string) {
  const query = useQuery({
    queryKey: ['web', 'filter-options', city ?? 'all'],
    queryFn: () => webApi.filterOptions(city ? { city } : undefined),
    staleTime: 5 * 60_000,
  })

  const options: ApiFilterOptions = {
    ...FALLBACK,
    ...query.data,
    categories: query.data?.categories?.length ? query.data.categories : FALLBACK.categories,
    bhkOptions: query.data?.bhkOptions?.length ? query.data.bhkOptions : FALLBACK.bhkOptions,
    furnishedOptions: query.data?.furnishedOptions?.length
      ? query.data.furnishedOptions
      : FALLBACK.furnishedOptions,
    saleTypeOptions: query.data?.saleTypeOptions?.length
      ? query.data.saleTypeOptions
      : FALLBACK.saleTypeOptions,
    budgetRanges: query.data?.budgetRanges?.length ? query.data.budgetRanges : FALLBACK.budgetRanges,
    sortOptions: query.data?.sortOptions?.length ? query.data.sortOptions : FALLBACK.sortOptions,
  }

  return { ...query, options }
}
